import React, { useEffect } from 'react';
import Grid from '@/components/molecules/Grid';
import { useTranslation } from 'react-i18next';
import { ResultH2 } from '@/pages/result/styles';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import StyledArticle from '@/components/templates/StyledArticle/StyledArticle';
import { ROUTES } from '@/constants/urls';
import {
	selectBlocks,
	selectIncorrectAnswers,
	selectIsTestHasBeenModified,
	selectIsUserAuth,
	selectMyDuration,
	selectResultData,
	selectResultDescription,
	selectResultFooter,
	selectResultHeader,
	selectResultRecommendations,
	selectResultTestId,
} from '@/store/selectors/resultSelectors';
import ResultFooterRecommendations
	from '@/components/features/result/ResultFooterRecommendations/ResultFooterRecommendations';
import ConditionalBlock from '@/components/features/result/ConditionalBlock/ConditionalBlock';
import WarningMessage from '@/components/atoms/UI/forms/WarningMessage/WarningMessage';
import {AdvResultTop } from '@/components/features/adv/AdResult/AdResult';
import ResultDuration from '@/components/features/result/ResultDuration/ResultDuration';
import Card from '@/components/atoms/UI/Card/Card';
import { Chart } from '@/pages/designSystem/Charts';
import AttributesCard from '@/components/features/cards/AttributesCard/AttributesCard';
import { sanitizeHTML } from '@/core/hooks/useSanitizedHTML';
import FlexContainer from '@/components/molecules/FlexContainer';
import Button from '@/components/atoms/UI/buttons/Button';
import SharePageButton from '@/components/features/SharePage/SharePage';
import IncorrectAnswers from '@/components/features/result/IncorrectAnswers/IncorrectAnswers';
import mathJaxConfig from '@/constants/mathJaxConfig';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import AdvFlocktory from '@/components/features/adv/AdResult/AdvFlocktory';
import { selectResultCountry } from '@/store/selectors/resultCountrySelector';

export const ResultHeader = styled.header``;
export const ResultMain = styled.main``;
export const ResultFooter = styled.footer`
`;

interface ResultDescriptionProps {
}

const ResultDescription: React.FC<ResultDescriptionProps> = ({}) => {
	const isProd = import.meta.env.PROD;
	const resultData = useSelector(selectResultData);
	const resultDescription = useSelector(selectResultDescription);
	const resultDescriptionHeader = useSelector(selectResultHeader);
	const sanitizedHeaderHTML = sanitizeHTML(resultDescriptionHeader);
	const isUserAuth = useSelector(selectIsUserAuth);
	const incorrectAnswers = useSelector(selectIncorrectAnswers);
	const myDuration = useSelector(selectMyDuration);
	const testId = useSelector(selectResultTestId);
	const country = useSelector(selectResultCountry);

	const resultDescriptionFooter = useSelector(selectResultFooter);
	const sanitizedFooterHTML = sanitizeHTML(resultDescriptionFooter);
	const resultDescriptionRecommendations = useSelector(selectResultRecommendations);
	const isTestHasBeenModified = useSelector(selectIsTestHasBeenModified);

	const blocks = useSelector(selectBlocks);
	const resultHeader = blocks.resultHeader;
	const resultFooter = blocks.resultFooter;
	const forResultNeedSpecialist = blocks.forResultNeedSpecialist;
	const advTop = blocks.advResultTop;
	const advBottom = blocks.advResultBottom;
	const advFlocktory = blocks.advFlocktory;

	const { t } = useTranslation();
	const text_your_results = t('result.your_results' as string);
	const text_test_modified = t('result.soft404' as string);

	const text_save = t('result.save');
	const text_saved = t('result.saved');

	const sanitizedDescriptions = React.useMemo(() => {
		return resultDescription.map((item: any) => ({
			id: item.id, // Уникальный идентификатор для key
			sanitizedText: sanitizeHTML(item.text), // Очистка HTML с использованием хука
		}));
	}, [resultDescription]);

	return (
		<>
			<Grid col='1' gap='2' >
				<Grid col='1' gap='3' >
					<ResultH2>{text_your_results}</ResultH2>
					<ConditionalBlock block={advFlocktory}><AdvFlocktory /></ConditionalBlock>

					{resultDescriptionHeader && sanitizedHeaderHTML && (
						<StyledArticle>
							<ConditionalBlock block={resultHeader}>
								<ResultHeader dangerouslySetInnerHTML={{ __html: sanitizedHeaderHTML }} />
							</ConditionalBlock>
						</StyledArticle>
					)}

					{/*<AttributesCard />*/}

					<ResultMain>
						<MathJaxContext version={3} config={mathJaxConfig}>
							<Grid col="1" gap="1">
								{/* Проверка, был ли тест модифицирован */}
								{isTestHasBeenModified ? (
									<WarningMessage>{text_test_modified}</WarningMessage>
								) : (
									// Используем заранее обработанный массив с использованием useMemo
									sanitizedDescriptions.map((item: any, index: number) => {
										// Проверка на последний или единственный элемент
										const isBeforeLastItem = index === sanitizedDescriptions.length - 2;
										const isSingleItem = sanitizedDescriptions.length === 1;

										return (
											<React.Fragment key={item.id || index}>
												<StyledArticle
													dangerouslySetInnerHTML={{ __html: item.sanitizedText }}
												/>

												{/* Вставка рекламы на последних элементах */}
												{(isBeforeLastItem || isSingleItem) && testId && (
													<ConditionalBlock block={advTop}>
														<AdvResultTop testId={testId} country={country} />
													</ConditionalBlock>
												)}
											</React.Fragment>
										);
									})
								)}

								{/* Неправильные ответы */}
								{incorrectAnswers.length > 0 && <IncorrectAnswers />}
							</Grid>
						</MathJaxContext>
					</ResultMain>

					{resultDescriptionFooter && sanitizedFooterHTML && (
						<StyledArticle>
							<ConditionalBlock block={resultFooter}>
								<ResultFooter dangerouslySetInnerHTML={{ __html: sanitizedFooterHTML }} />
							</ConditionalBlock>
						</StyledArticle>
					)}

					{forResultNeedSpecialist.content && <StyledArticle>
						<ConditionalBlock block={forResultNeedSpecialist}>
							<p dangerouslySetInnerHTML={{ __html: sanitizeHTML(forResultNeedSpecialist.content) }}/>
						</ConditionalBlock>
					</StyledArticle>}
				</Grid>

				{/*<Chart />*/}

				{myDuration > 0 && <ResultDuration/>}

				{resultDescriptionRecommendations
					&& <ResultFooterRecommendations recommend_for_you={resultDescriptionRecommendations}/>
				}

				{/*<ConditionalBlock block={advBottom}>*/}
				{/*	/!*{isAdvShown  && testId && <AdvResultTop testId={testId}/>}*!/*/}
				{/*</ConditionalBlock>*/}

				<FlexContainer gap={'1'} margin={'2'} stretchMobileOnly>
					{isUserAuth
						? <Button success autoWidth href={ROUTES.MY_TESTS}>{text_saved}</Button>
						: <Button autoWidth href={ROUTES.LOGIN}>{text_save}</Button>
					}
					<SharePageButton />
				</FlexContainer>
			</Grid>
		</>

	)
}

export default ResultDescription